import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import {
  GrievanceDetail,
  grievanceApi,
  GrievancePriority,
  GrievanceStatus,
} from "./grievanceApi";

const statusColor: Record<GrievanceStatus, "default" | "primary" | "warning" | "success" | "error"> = {
  OPEN: "error",
  IN_PROGRESS: "warning",
  RESOLVED: "success",
  CLOSED: "default",
};

const priorityColor: Record<GrievancePriority, "default" | "primary" | "warning" | "success" | "error"> = {
  LOW: "default",
  MEDIUM: "primary",
  HIGH: "error",
};

const GrievanceView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<GrievanceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    try {
      const res = await grievanceApi.getById(id);
      setData(res);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleAddComment = async () => {
    if (!id || !comment.trim()) {
      return;
    }
    setSaving(true);
    try {
      const res = await grievanceApi.addComment(id, { comment });
      setData(res);
      setComment("");
    } catch {
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/scholar-register/grievance")}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={500}>
            Grievance Details
          </Typography>
        </Box>
        {loading && (
          <Typography color="textSecondary">
            Loading grievance details...
          </Typography>
        )}
        {!loading && (
          <Typography color="textSecondary">Grievance not found.</Typography>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/scholar-register/grievance")}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={500}>
          Grievance Details
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Ticket Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">
                    Ticket Number
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data.ticketNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {new Date(data.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">
                    Student
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data.student
                      ? `${data.student.firstName} ${data.student.lastName}`
                      : "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">
                    Class / Section
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data.class ? data.class.name : "-"}{" "}
                    {data.section ? `- ${data.section.name}` : ""}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="textSecondary">
                    Type
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data.type === "GRIEVANCE" ? "Grievance" : "Suggestion"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="textSecondary">
                    Category
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data.category}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="textSecondary">
                    Priority
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      size="small"
                      label={data.priority}
                      color={priorityColor[data.priority]}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      size="small"
                      label={data.status.replace("_", " ")}
                      color={statusColor[data.status]}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="caption" color="textSecondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data.assignedTo
                      ? `${data.assignedTo.firstName} ${data.assignedTo.lastName}`
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" whiteSpace="pre-line">
                {data.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {data.attachmentUrl && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Attachment
                </Typography>
                <Button
                  href={data.attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View / Download Attachment
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Conversation Thread
              </Typography>
              <List dense>
                {data.comments.map((c) => (
                  <React.Fragment key={c.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={500}>
                              {c.user
                                ? `${c.user.firstName} ${c.user.lastName}`
                                : "User"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              {new Date(c.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            whiteSpace="pre-line"
                          >
                            {c.comment}
                          </Typography>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
                {data.comments.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography color="textSecondary">
                          No comments yet.
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
              <Box mt={2} display="flex" gap={2}>
                <TextField
                  fullWidth
                  placeholder="Add a comment"
                  size="small"
                  multiline
                  minRows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={saving}
                >
                  Add
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Resolution
              </Typography>
              {data.resolution ? (
                <Typography variant="body1" whiteSpace="pre-line">
                  {data.resolution}
                </Typography>
              ) : (
                <Typography color="textSecondary">
                  Resolution not added yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GrievanceView;

